import React from 'react';
import {
  formatDate,
  formatMarketCap,
  formatPercent,
  formatTimeAgo,
  getTrackerLabel,
} from '../utils/trackerUtils';
import TierBadge from './TierBadge';

function TagList({ items, kind = 'recovery', empty = 'None yet' }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  };

  if (!items?.length) {
    return <span className="text-xs text-gray-400">{empty}</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {[...items].sort((a, b) => a - b).map((item) => (
        <span
          key={item}
          className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
            kind === 'multiplier' ? colors.purple : colors.blue
          }`}
        >
          {kind === 'multiplier' ? `${item}x` : `+${item}%`}
        </span>
      ))}
    </div>
  );
}

export default function TrackerDetailModal({
  isOpen,
  onClose,
  tracker,
  tokenSymbol,
  currentMarketCap,
  recoverySnapshot,
}) {
  if (!isOpen || !tracker) return null;

  const gainFromInitial = tracker.gainFromInitial ?? (
    tracker.initialMarketCap && currentMarketCap
      ? ((currentMarketCap - tracker.initialMarketCap) / tracker.initialMarketCap) * 100
      : null
  );

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Tracker details
              </div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {tokenSymbol ? `${tokenSymbol} · ` : ''}{getTrackerLabel(tracker)}
              </h2>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
                Chat {tracker.chatId}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1"
            >
              ✕
            </button>
          </div>

          <div className="px-5 py-4 space-y-4">
            <div className="flex flex-wrap gap-2">
              <TierBadge tier={tracker.tier} />
              {tracker.isRecovering ? (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                  📈 Recovering
                </span>
              ) : (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  👀 Watching
                </span>
              )}
              {tracker.recoveryStartAlertSent && (
                <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                  Start alert sent
                </span>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Scanned</div>
                <div className="font-medium text-gray-900 dark:text-white">{formatDate(tracker.addedAt)}</div>
                <div className="text-xs text-gray-400">{formatTimeAgo(tracker.addedAt)}</div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Caller</div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {tracker.addedBy || tracker.sharedBy || '—'}
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Initial MC</div>
                <div className="font-medium text-gray-900 dark:text-white">
                  {formatMarketCap(tracker.initialMarketCap)}
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Gain from IMC</div>
                <div className={`font-bold ${gainFromInitial >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatPercent(gainFromInitial)}
                </div>
              </div>
            </div>

            {recoverySnapshot && (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
                  Shared recovery episode
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-gray-500">Bottom</div>
                    <div className="font-medium text-red-600">{formatMarketCap(recoverySnapshot.bottomMarketCap)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Current</div>
                    <div className="font-medium">{formatMarketCap(currentMarketCap)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Recovery</div>
                    <div className="font-medium text-green-600">
                      {formatPercent(recoverySnapshot.recoveryPercentage)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">From ATH</div>
                    <div className="font-medium">
                      {recoverySnapshot.recoveryFromAthPercentage?.toFixed?.(1) ?? '—'}%
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
                Milestone alerts for this scan
              </div>
              <TagList items={tracker.milestonesReached} kind="multiplier" empty="No milestone alerts yet" />
            </div>

            <div>
              <div className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">
                Recovery milestones for this group
              </div>
              <TagList
                items={tracker.recoveryMilestonesReached}
                kind="recovery"
                empty="No recovery milestones yet"
              />
            </div>

            {tracker.recoveryStartedAt && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Recovery started {formatDate(tracker.recoveryStartedAt)} ({formatTimeAgo(tracker.recoveryStartedAt)})
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
